import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { MessageService } from 'primeng/api';
import { ActivatedRoute, Router } from '@angular/router';
import { map, startWith } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { AsyncPipe, NgIf } from '@angular/common';
import { LanguageTypes } from '@shared/enums';
import { CurrencyTypes, LocationFormCardComponent } from '@ep/shared';
import { LocationFormComponent } from '@app/areas/merchants/forms/location-form/location-form.component';
import { LocationFormService } from '@app/areas/merchants/services/location-form.service';
import { LocationsApiService } from '@app/areas/merchants/services/locations.api.service';

@Component({
  selector: 'app-new-location',
  templateUrl: './new-location.component.html',
  styleUrls: ['./new-location.component.scss'],
  standalone: true,
  imports: [LocationFormComponent, LocationFormCardComponent, AsyncPipe, NgIf],
})
export class NewLocationComponent implements OnInit, OnDestroy {
  private readonly locationFormService = inject(LocationFormService);
  private readonly locationsApiService = inject(LocationsApiService);
  private readonly messageService = inject(MessageService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);

  protected isSaveDisabled$ = this.locationFormService.getForm().statusChanges.pipe(
    startWith(this.locationFormService.getForm().status),
    map((status) => status !== 'VALID')
  );

  protected isSaving = false;
  private subs = new Subscription();

  ngOnInit(): void {
    this.locationFormService.initializeForAdd();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  protected saveLocation(): void {
    const form = this.locationFormService.getForm();
    if (!form.valid) {
      return;
    }

    const merchantId = Number(this.route.parent?.parent?.snapshot.paramMap.get('merchantId'));
    this.isSaving = true;
    const formValue = form.getRawValue();

    this.subs.add(
      this.locationsApiService
        .create({
          MerchantId: merchantId,
          Name: formValue.Name!,
          Address: formValue.Address!,
          PhoneNumber: Number(formValue.PhoneNumber?.replace(/\D/g, '')),
          TimeZoneType: formValue.TimeZoneType!,
          CurrencyType: CurrencyTypes.Usd,
          LanguageType: LanguageTypes.English,
        })
        .subscribe((location) => {
          this.isSaving = false;
          if (location) {
            this.router.navigate(['../', location.LocationId], { relativeTo: this.route });
          } else {
            this.messageService.add({
              severity: 'error',
              summary: 'ERROR: New Location',
              detail: 'Something went wrong while creating the location',
            });
          }
        })
    );
  }
}
